<template>
  <div
    class="flex flex-col rounded-t-lg w-[375px] pointer-events-auto"
    :class="chatEntry.expanded ? 'embedded_chat_window' : ''"
  >
    <AddOrEditTaskModal
      v-model:visible="showTaskModal"
      :messageId="taskMessageId"
      :groupId="chat?.id || null"
      :initialData="taskInitialData"
      :messageContext="taskMessageContext"
      @task-created="showTaskModal = false"
    />
    <!-- Top Bar (handles both collapsed and expanded states) -->
    <EmbeddedChatTopBar
      :active-chat="chat"
      :group-members="chatEntry.groupMembers"
      :total-member-count="chatEntry.totalMemberCount"
      :expanded="chatEntry.expanded"
      @collapse="toggleChat"
      @expand="toggleChat"
      @close="closeChat"
    />

    <!-- Expanded Chat Body (animated) -->
    <div
      class="embedded_chat_body relative"
      :class="chatEntry.expanded ? 'embedded_chat_body--open' : 'embedded_chat_body--closed'"
      :style="chatEntry.expanded ? { height: maxBodyHeight + 'px' } : {}"
      @dragenter.prevent="onDragEnter"
      @dragover.prevent
      @dragleave="onDragLeave"
      @drop.prevent="onDrop"
    >
      <div v-if="isDragOver" class="drag-drop-overlay">
        <div class="drag-drop-content">
          <div class="drag-drop-icon">+</div>
          <span class="drag-drop-label">Drop files to upload</span>
        </div>
      </div>

      <!-- ===== THREAD VIEW ===== -->
      <div v-if="chatEntry.threadOpen" class="flex flex-col flex-1 min-h-0">
        <!-- Thread Header -->
        <div class="flex items-center gap-2 px-3 py-2 flex-shrink-0 border_bottom" style="background-color: var(--background-color)">
          <ThemeImage
            :icon="imageNames.BACK_ARROW_DEFAULT_GREY"
            class="size-4 cursor-pointer"
            alt="back"
            @click="closeThread"
          />
          <span class="text-sm font-semibold primary_color">Thread</span>
        </div>

        <!-- Thread Messages -->
        <div
          ref="threadMessagesContainer"
          class="flex flex-col flex-1 min-h-0 overflow-y-auto overflow-x-hidden"
          style="background-color: var(--background-color)"
        >
          <div class="flex-grow"></div>

          <div v-if="chatEntry.threadLoading" class="flex items-center justify-center py-8">
            <span class="text-sm secondary_color">Loading thread...</span>
          </div>

          <div
            v-if="!chatEntry.threadLoading"
            v-for="(message, index) in threadMessages"
            :key="'thread_' + message?.id"
            :class="[
              'flex flex-col',
              { 'border_bottom': index === 0 }
            ]"
          >
            <TaskMessage
              v-if="message?.task_id"
              source="embedded_thread"
              :task="message.task || null"
              :message="message"
              :chat="chat"
            />

            <div
              v-else
              :class="[
                'flex flex-row gap-[6px] px-4 group relative message',
                isGroupedMessage(index, true) ? 'py-[2px]' : 'py-2',
              ]"
              @mouseenter="hoveredMessageId = message.id"
              @mouseleave="handleMessageLeave"
            >
              <ProfileIcon
                v-if="!isGroupedMessage(index, true)"
                :data="getMessageSender(message)"
                :font="12"
                :size="28"
              />
              <div v-else class="shrink-0" style="width: 28px"></div>

              <div class="flex flex-col gap-2 flex-1 min-w-0">
                <div v-if="!isGroupedMessage(index, true)" class="flex items-center gap-2">
                  <span class="text-[15px] font-semibold primary_color">{{ getMessageSenderName(message) }}</span>
                  <span class="text-[11px] secondary_color">{{ formatTimestamp(message?.sent_at, "timestamp", formatTypes.centered) }}</span>
                  <span v-if="message?.is_forwarded" class="text-[12px] font-normal secondary_color italic">Forwarded</span>
                </div>

                <div
                  v-if="message?.text"
                  :class="[
                    'text-[15px] font-weight-regular break-words whitespace-pre-wrap leading-normal',
                    isSystemMessage(message) ? 'secondary_color opacity-70' : 'primary_color'
                  ]"
                  v-html="renderMessageText(message)"
                  @click="(e) => handleMessageContentClick(e, message)"
                ></div>

                <span v-if="message?.edited" class="text-[12px] secondary_color">(edited)</span>

                <AttachmentRenderer
                  v-if="message?.attachments?.length > 0"
                  source="embedded_thread"
                  :attachments="formatAttachmentsForDisplay(message.attachments)"
                  :type="attachmentRendererType.PREVIEW"
                  background-color="transparent"
                  @attachment-click="handleDownloadAttachment"
                />

                <div
                  v-if="message?.reactions && Object.keys(message.reactions).length"
                  class="flex flex-row gap-2 flex-wrap"
                >
                  <a-tooltip
                    v-for="(count, emoji) in message.reactions"
                    :key="message.id + '_' + emoji"
                    v-if="count > 0 && emoji !== 'undefined' && emoji != null"
                    :title="getReactionTooltip(message.id, emoji)"
                    placement="top"
                    @visibleChange="(visible) => visible && fetchReactionUsers(message.id, emoji)"
                  >
                    <div
                      :class="['flex flex-row items-center gap-[6px] px-[7px] rounded-xl bg_8', isCurrentUserMember ? 'cursor-pointer hover:border_12' : 'cursor-default']"
                      @click="handleReactionClick(message.id, emoji)"
                    >
                      <span class="text-base pt-[2px]">{{ emoji }}</span>
                      <span class="text-xs font-normal primary_color">{{ count }}</span>
                    </div>
                  </a-tooltip>
                </div>

                <div
                  v-if="message?.also_sent_to_channel"
                  class="text-xs secondary_color mt-1"
                >
                  Also sent to {{ chat?.is_dm ? 'conversation' : 'channel' }}
                </div>
              </div>

              <div
                v-show="activeMessageId === message.id && !message?.is_system && !message?.task_id && isCurrentUserMember"
                class="absolute right-2 -top-3 opacity-0 group-hover:opacity-100 transition-opacity duration-150 z-10"
                @mouseenter="isOnActions = true"
                @mouseleave="handleActionsLeave"
              >
                <MessageActions
                  source="embedded_thread"
                  :id="message.id"
                  :thread-id="chatEntry.threadId"
                  :is-broadcast-message="!!message.broadcast_from_thread_id"
                  :message-content="message.text"
                  :message-sender-id="message.user_id"
                  :message-attachments="message.attachments || []"
                  :chat="chat"
                  @dropdown-open="handleDropdownOpen(message.id)"
                  @dropdown-close="handleDropdownClose"
                  @mouse-enter="isOnActions = true"
                  @mouse-leave="handleActionsLeave"
                  @edit-message="startThreadEdit($event.id, $event.text)"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Thread Input -->
        <div v-if="isCurrentUserMember" class="flex-shrink-0 px-2 pb-2" style="background-color: var(--background-color)">
          <div v-if="!editingThreadMessageId" class="flex flex-row items-center gap-2 px-2 py-1">
            <a-checkbox v-model:checked="alsoSendToChannel" size="small">
              <span class="text-xs primary_color">Also send to {{ chat?.is_dm ? 'conversation' : 'channel' }}</span>
            </a-checkbox>
          </div>
          <div class="flex flex-col rounded-lg bg_4 border_10">
            <div
              v-if="editingThreadMessageId"
              class="flex items-center justify-between px-3 py-2 border-b border_10"
            >
              <span class="text-xs secondary_color">Editing message</span>
              <span
                class="text-xs secondary_color cursor-pointer hover:primary_color"
                @click="cancelThreadEdit"
              >✕ Cancel</span>
            </div>

            <div
              v-if="threadAttachments?.length > 0"
              class="overflow-y-auto"
              style="max-height: 150px"
            >
              <AttachmentRenderer
                source="embedded_thread_input"
                sectionCSS="pt-2 px-2"
                background-color="var(--background-color)"
                :attachments="threadAttachments"
                :type="attachmentRendererType.UPLOAD"
                @remove-attachment="removeThreadAttachment"
              />
            </div>

            <div class="flex flex-col relative">
              <a-textarea
                ref="threadTextareaRef"
                class="!resize-none !border-none !bg-transparent !shadow-none mt-[6px]"
                v-model:value="threadMessageText"
                :placeholder="editingThreadMessageId ? 'Edit your message' : 'Reply in thread'"
                :auto-size="{ minRows: 1, maxRows: 4 }"
                @keydown="handleThreadKeyDown"
                @paste="handleThreadPaste"
                @input="checkForMention('thread')"
                @click="checkForMention('thread')"
                @keyup.left="checkForMention('thread')"
                @keyup.right="checkForMention('thread')"
              />

              <div
                v-if="showMentionDropdown && mentionActiveInput === 'thread'"
                ref="mentionDropdownRef"
                :class="[
                  'mention-dropdown absolute left-0 w-64 border_10 rounded-lg shadow-lg overflow-hidden z-50',
                  'bottom-full mb-1'
                ]"
                style="background-color: var(--background-color)"
              >
                <div class="p-2 border-b border_10">
                  <input
                    ref="mentionSearchRef"
                    v-model="mentionSearch"
                    type="text"
                    placeholder="Search users..."
                    class="w-full px-2 py-1 text-sm bg-transparent border_10 rounded primary_color"
                    @keydown.down.prevent="navigateMentions(1)"
                    @keydown.up.prevent="navigateMentions(-1)"
                    @keydown.enter.prevent="selectMention"
                    @keydown.esc="closeMentionDropdown"
                  />
                </div>
                <div class="max-h-48 overflow-y-auto">
                  <div
                    v-for="(item, idx) in mentionItems"
                    :key="'thread_' + item.id"
                    :class="[
                      'flex items-center gap-2 px-3 py-2 cursor-pointer transition-colors',
                      selectedMentionIndex === idx ? 'bg_8' : 'hover:bg_6'
                    ]"
                    @mouseenter="selectedMentionIndex = idx"
                    @click="selectMention"
                  >
                    <div v-if="item.isGroupMention" class="flex items-center justify-center rounded-full bg-[var(--white-opacity-14)]" style="width: 24px; height: 24px;">
                      <span class="text-xs primary_color font-semibold">@</span>
                    </div>
                    <span v-if="item.isGroupMention" class="text-sm primary_color font-medium">@{{ item.name }}</span>
                    <ProfileIcon v-if="!item.isGroupMention" :data="item" :size="24" :font="10" />
                    <span v-if="!item.isGroupMention" class="text-sm primary_color">{{ renderEntity(item) }}</span>
                  </div>
                  <div v-if="mentionItems.length === 0" class="px-3 py-2 text-sm secondary_color text-center">
                    No users found
                  </div>
                </div>
              </div>
            </div>

            <div class="flex flex-row items-center justify-between p-2">
              <div class="flex flex-row items-center gap-[6px]">
                <a-tooltip title="Attach file" placement="top">
                  <AttachmentButton
                    source="embedded_thread_input"
                    label=""
                    :multiple="true"
                    @file-selected="handleThreadFileSelect"
                    visible
                  />
                </a-tooltip>

                <a-tooltip placement="top" v-model:open="showThreadEmojiPicker" trigger="click">
                  <template #title><EmojiPicker :native="true" @select="insertThreadEmoji" /></template>
                  <ThemeImage :icon="imageNames.EMOJI_GREY" class="size-5 cursor-pointer" alt="emoji_icon" />
                </a-tooltip>
              </div>

              <a-button
                class="!text-sm primary_small"
                :disabled="!threadMessageText?.trim() && !(threadAttachments?.length > 0)"
                @click="sendThreadReply"
              >
                {{ editingThreadMessageId ? 'Save' : 'Reply' }}
                <ThemeImage class="size-5" :icon="imageNames.SEND_BLACK" alt="send_black" />
              </a-button>
            </div>
          </div>
        </div>
        <div v-else class="flex items-center justify-center px-3 py-2 flex-shrink-0 border_top" style="background-color: var(--background-color)">
          <span class="text-xs secondary_color">You need to join this group to send messages</span>
        </div>
      </div>

      <!-- ===== NORMAL CHAT VIEW ===== -->
      <div v-else class="flex flex-col flex-1 min-h-0">
        <!-- Messages -->
        <div
          ref="messagesContainer"
          class="flex flex-col flex-1 min-h-0 overflow-y-auto overflow-x-hidden"
          style="background-color: var(--background-color)"
          @scroll="handleScroll"
        >
          <div class="flex-grow"></div>

          <div v-if="chatEntry.loadingMore" class="flex justify-center py-3">
            <span class="text-xs secondary_color">Loading older messages...</span>
          </div>

          <div v-if="chatEntry.loading" class="flex items-center justify-center py-8">
            <span class="text-sm secondary_color">Loading...</span>
          </div>

          <div
            v-if="!chatEntry.loading"
            v-for="(message, index) in filteredMessages"
            :key="message?.id"
            class="flex flex-col"
          >
            <div v-if="shouldShowDateDivider(index)" class="flex items-center px-4 py-3">
              <div class="flex-1 h-px" style="background-color: var(--white-opacity-8)"></div>
              <span class="text-[11px] font-medium secondary_color whitespace-nowrap px-3 py-1 rounded-full bg_8">
                {{ getDateDividerLabel(message.sent_at) }}
              </span>
              <div class="flex-1 h-px" style="background-color: var(--white-opacity-8)"></div>
            </div>

            <TaskMessage
              v-if="message?.task_id"
              source="embedded_chat"
              :task="message.task || null"
              :message="message"
              :chat="chat"
            />

            <div
              v-else
              :class="[
                'flex flex-row gap-[6px] px-4 group relative message',
                isGroupedMessage(index) ? 'py-[2px]' : 'py-2',
              ]"
              @mouseenter="hoveredMessageId = message.id"
              @mouseleave="handleMessageLeave"
            >
              <ProfileIcon
                v-if="!isGroupedMessage(index)"
                :data="getMessageSender(message)"
                :font="12"
                :size="28"
              />
              <div v-else class="shrink-0" style="width: 28px"></div>

              <div class="flex flex-col gap-2 flex-1 min-w-0">
                <div v-if="!isGroupedMessage(index)" class="flex items-center gap-2">
                  <span class="text-[15px] font-semibold primary_color">{{ getMessageSenderName(message) }}</span>
                  <span class="text-[11px] secondary_color">{{ formatTimestamp(message?.sent_at, "timestamp", formatTypes.centered) }}</span>
                  <span v-if="message?.is_forwarded" class="text-[12px] font-normal secondary_color italic">Forwarded</span>
                </div>

                <div
                  v-if="message?.broadcast_from_thread_id"
                  class="flex gap-1 items-baseline min-w-0"
                >
                  <span class="text-[15px] secondary_color shrink-0 whitespace-nowrap">replied to thread:</span>
                  <div
                    class="text-[15px] font-medium cursor-pointer hover:underline truncate min-w-0"
                    style="color: var(--link-color);"
                    @click="openThreadFromBroadcast(message)"
                    :title="getThreadMessagePreview(message)"
                  >
                    {{ getThreadMessagePreview(message) }}
                  </div>
                </div>

                <div
                  v-if="message?.text"
                  :class="[
                    'text-[15px] font-weight-regular break-words whitespace-pre-wrap leading-normal',
                    isSystemMessage(message) ? 'secondary_color opacity-70' : 'primary_color'
                  ]"
                  v-html="renderMessageText(message)"
                  @click="(e) => handleMessageContentClick(e, message)"
                ></div>

                <span v-if="message?.edited" class="text-[12px] secondary_color">(edited)</span>

                <AttachmentRenderer
                  v-if="message?.attachments?.length > 0"
                  source="embedded_chat"
                  :attachments="formatAttachmentsForDisplay(message.attachments)"
                  :type="attachmentRendererType.PREVIEW"
                  background-color="transparent"
                  @attachment-click="handleDownloadAttachment"
                />

                <div
                  v-if="message?.reactions && Object.keys(message.reactions).length"
                  class="flex flex-row gap-2 flex-wrap"
                >
                  <a-tooltip
                    v-for="(count, emoji) in message.reactions"
                    :key="message.id + '_' + emoji"
                    v-if="count > 0 && emoji !== 'undefined' && emoji != null"
                    :title="getReactionTooltip(message.id, emoji)"
                    placement="top"
                    @visibleChange="(visible) => visible && fetchReactionUsers(message.id, emoji)"
                  >
                    <div
                      :class="['flex flex-row items-center gap-[6px] px-[7px] rounded-xl bg_8', isCurrentUserMember ? 'cursor-pointer hover:border_12' : 'cursor-default']"
                      @click="handleReactionClick(message.id, emoji)"
                    >
                      <span class="text-base pt-[2px]">{{ emoji }}</span>
                      <span class="text-xs font-normal primary_color">{{ count }}</span>
                    </div>
                  </a-tooltip>
                </div>

                <span
                  v-if="message?.reply_count > 0"
                  class="text-[11px] font-bold cursor-pointer hover:underline"
                  style="color: var(--link-color);"
                  @click="openThread(message)"
                >
                  {{ message.reply_count }} {{ message.reply_count === 1 ? 'reply' : 'replies' }}
                </span>
              </div>

              <div
                v-show="activeMessageId === message.id && !message?.is_system && !message?.task_id && isCurrentUserMember"
                class="absolute right-2 -top-3 opacity-0 group-hover:opacity-100 transition-opacity duration-150 z-10"
                @mouseenter="isOnActions = true"
                @mouseleave="handleActionsLeave"
              >
                <MessageActions
                  source="embedded_chat"
                  :id="message.id"
                  :thread-id="message.broadcast_from_thread_id ? getThreadRootId(message) : message.thread_id"
                  :is-broadcast-message="!!message.broadcast_from_thread_id"
                  :message-content="message.text"
                  :message-sender-id="message.user_id"
                  :message-attachments="message.attachments || []"
                  :chat="chat"
                  @dropdown-open="handleDropdownOpen(message.id)"
                  @dropdown-close="handleDropdownClose"
                  @mouse-enter="isOnActions = true"
                  @mouse-leave="handleActionsLeave"
                  @edit-message="startEditMessage($event.id, $event.text)"
                  @open-thread="({ threadId }) => openThread({ thread_id: threadId, id: threadId })"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Input -->
        <div v-if="isCurrentUserMember" class="flex-shrink-0 px-2 pb-2" style="background-color: var(--background-color)">
          <div class="flex flex-col rounded-lg bg_4 border_10">
            <div
              v-if="editingMessageId"
              class="flex items-center justify-between px-3 py-2 border-b border_10"
            >
              <span class="text-xs secondary_color">Editing message</span>
              <span
                class="text-xs secondary_color cursor-pointer hover:primary_color"
                @click="cancelEdit"
              >✕ Cancel</span>
            </div>

            <div
              v-if="attachments?.length > 0"
              class="overflow-y-auto"
              style="max-height: 150px"
            >
              <AttachmentRenderer
                source="embedded_chat_input"
                sectionCSS="pt-2 px-2"
                background-color="var(--background-color)"
                :attachments="attachments"
                :type="attachmentRendererType.UPLOAD"
                @remove-attachment="removeAttachment"
              />
            </div>

            <div class="flex flex-col relative">
              <a-textarea
                ref="textareaRef"
                class="!resize-none !border-none !bg-transparent !shadow-none mt-[6px]"
                v-model:value="messageText"
                :placeholder="editingMessageId ? 'Edit your message' : 'Write a message'"
                :auto-size="{ minRows: 1, maxRows: 4 }"
                @keydown="handleKeyDown"
                @paste="handlePaste"
                @input="() => checkForMention('main')"
                @click="() => checkForMention('main')"
                @keyup.left="() => checkForMention('main')"
                @keyup.right="() => checkForMention('main')"
              />

              <div
                v-if="showMentionDropdown && mentionActiveInput === 'main'"
                ref="mentionDropdownRef"
                :class="[
                  'mention-dropdown absolute left-0 w-64 border_10 rounded-lg shadow-lg overflow-hidden z-50',
                  mentionDropdownDirection === 'up' ? 'bottom-full mb-1' : 'top-full mt-1'
                ]"
                style="background-color: var(--background-color)"
              >
                <div class="p-2 border-b border_10">
                  <input
                    ref="mentionSearchRef"
                    v-model="mentionSearch"
                    type="text"
                    placeholder="Search users..."
                    class="w-full px-2 py-1 text-sm bg-transparent border_10 rounded primary_color"
                    @keydown.down.prevent="navigateMentions(1)"
                    @keydown.up.prevent="navigateMentions(-1)"
                    @keydown.enter.prevent="selectMention"
                    @keydown.esc="closeMentionDropdown"
                  />
                </div>
                <div class="max-h-48 overflow-y-auto">
                  <div
                    v-for="(item, idx) in mentionItems"
                    :key="item.id"
                    :class="[
                      'flex items-center gap-2 px-3 py-2 cursor-pointer transition-colors',
                      selectedMentionIndex === idx ? 'bg_8' : 'hover:bg_6'
                    ]"
                    @mouseenter="selectedMentionIndex = idx"
                    @click="selectMention"
                  >
                    <div v-if="item.isGroupMention" class="flex items-center justify-center rounded-full bg-[var(--white-opacity-14)]" style="width: 24px; height: 24px;">
                      <span class="text-xs primary_color font-semibold">@</span>
                    </div>
                    <span v-if="item.isGroupMention" class="text-sm primary_color font-medium">@{{ item.name }}</span>
                    <ProfileIcon v-if="!item.isGroupMention" :data="item" :size="24" :font="10" />
                    <span v-if="!item.isGroupMention" class="text-sm primary_color">{{ renderEntity(item) }}</span>
                  </div>
                  <div v-if="mentionItems.length === 0" class="px-3 py-2 text-sm secondary_color text-center">
                    No users found
                  </div>
                </div>
              </div>
            </div>

            <div class="flex flex-row items-center justify-between p-2">
              <div class="flex flex-row items-center gap-[6px]">
                <a-tooltip title="Attach file" placement="top">
                  <AttachmentButton
                    source="embedded_chat_input"
                    label=""
                    :multiple="true"
                    @file-selected="handleFileSelect"
                    visible
                  />
                </a-tooltip>

                <a-tooltip placement="top" v-model:open="showEmojiPicker" trigger="click">
                  <template #title><EmojiPicker :native="true" @select="insertEmoji" /></template>
                  <ThemeImage
                    :icon="imageNames.EMOJI_GREY"
                    class="size-5 cursor-pointer"
                    alt="emoji_icon"
                  />
                </a-tooltip>
              </div>

              <a-button
                class="!text-sm primary_small"
                :disabled="disableSendButton"
                @click="sendMessage"
              >
                {{ editingMessageId ? 'Save' : 'Send' }}

                <ThemeImage
                  class="size-5"
                  :icon="imageNames.SEND_BLACK"
                  alt="send_black"
                />
              </a-button>
            </div>
          </div>
        </div>
        <div v-else class="flex items-center justify-center px-3 py-2 flex-shrink-0 border_top" style="background-color: var(--background-color)">
          <span class="text-xs secondary_color">You need to join this group to send messages</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch, nextTick, onMounted, onBeforeUnmount } from "vue";
import { useStore } from "vuex";
import { imageNames } from "@/src/constants/imageConstants.js";
import { formatTimestamp } from "@/src/utils/dateTimeUtils.js";
import { formatTypes } from "@/src/constants/dateTimeConstants.js";
import { getRecipientType } from "@/src/utils/recipientUtils.js";
import { chatGroupVisibilityOptions } from "@/src/constants/chatConstants.js";
import { renderEntity } from "@/src/utils/utils.js";
import { createPlaceholderDM, downloadAttachment } from "@/src/utils/chatUtils.js";
import {
  isSystemMessage,
  filterAndSortMessages,
  isGroupedMessage as isGroupedMsg,
  shouldShowDateDivider as showDateDivider,
  getDateDividerLabel,
  getMessageSender,
  getMessageSenderName,
  renderMessageText,
  formatAttachmentsForDisplay,
} from "@/src/utils/chatMessageUtils.js";
import { message } from "ant-design-vue";
import { attachmentRendererType } from "@/src/constants/attachmentConstants.js";
import { uploadDownloadAPITypes } from "@/src/constants/commonConstants.js";
import { useFileUpload } from "@/src/composables/useFileUpload.js";
import ChatService from "@/src/api/ChatService.js";
import DashboardService from "@/src/api/DashboardService.js";
import ThemeImage from "@/src/components/ThemeImage.vue";
import ProfileIcon from "@/src/components/ProfileIcon.vue";
import AttachmentRenderer from "@/src/components/attachment_components/AttachmentRenderer.vue";
import AttachmentButton from "@/src/components/attachment_components/AttachmentButton.vue";
import EmbeddedChatTopBar from "@/src/components/projects_components/chats/EmbeddedChatTopBar.vue";
import MessageActions from "@/src/components/projects_components/chats/messages/MessageActions.vue";
import TaskMessage from "@/src/components/projects_components/chats/messages/TaskMessage.vue";
import AddOrEditTaskModal from "@/src/components/task_components/AddOrEditTaskModal.vue";
import EmojiPicker from "vue3-emoji-picker";
import { debounce } from "lodash";
import eventBus from "@/src/utils/eventBus.js";

const store = useStore();

const props = defineProps({
  chatEntry: { type: Object, required: true },
  maxBodyHeight: { type: Number, default: 500 },
});

const chat = computed(() => props.chatEntry.chat);
const currentUserId = computed(() => store.getters["auth/userData"]?.id);
const allUsers = computed(() => store.getters["users/users"] || []);
const isCurrentUserMember = computed(() => {
  if (!chat.value) return false;
  if (chat.value.is_placeholder) return true;
  if (chat.value.type !== chatGroupVisibilityOptions.PUBLIC) return true;
  const members = props.chatEntry.groupMembers || [];
  if (members.length === 0) return true;
  return members.some((m) => Number(m.user_id) === Number(currentUserId.value));
});
const messagesContainer = ref(null);
const threadMessagesContainer = ref(null);
const messageText = ref("");
const threadMessageText = ref("");
const textareaRef = ref(null);
const threadTextareaRef = ref(null);
const isLoadingOlder = ref(false);
const savedScrollPosition = ref(null);
const showEmojiPicker = ref(false);
const showThreadEmojiPicker = ref(false);
const alsoSendToChannel = ref(false);
const editingMessageId = ref(null);
const editingOriginalText = ref("");
const showTaskModal = ref(false);
const taskMessageId = ref(null);
const taskInitialData = ref(null);
const taskMessageContext = ref(null);

// Mention state
const showMentionDropdown = ref(false);
const mentionSearch = ref("");
const mentionSearchRef = ref(null);
const mentionDropdownRef = ref(null);
const mentionStartPos = ref(0);
const selectedMentionIndex = ref(0);
const mentionedUserIds = ref([]);
const hasGroupMention = ref(false);
const threadMentionedUserIds = ref([]);
const threadHasGroupMention = ref(false);
const mentionDropdownDirection = ref("up");
const mentionActiveInput = ref("main"); // "main" or "thread"

const isOneOnOneDM = computed(() => {
  if (!chat.value) return false;
  return chat.value.is_dm === true
    && (chat.value.type === "dm" || chat.value.type === "self_dm")
    && chat.value.recipient_type === 1;
});

const groupMentionOptions = computed(() => {
  if (isOneOnOneDM.value) return [];
  const search = mentionSearch.value.toLowerCase().trim();
  const options = [
    { id: "__here__", name: "here", isGroupMention: true },
    { id: "__everyone__", name: "everyone", isGroupMention: true },
  ];
  if (!search) return options;
  return options.filter((o) => o.name.includes(search));
});

const filteredMentionUsers = computed(() => {
  const search = mentionSearch.value.toLowerCase().trim();
  return allUsers.value.filter((user) => {
    if (user.id === currentUserId.value) return false;
    const name = renderEntity(user).toLowerCase();
    return name.includes(search);
  });
});

const mentionItems = computed(() => [...groupMentionOptions.value, ...filteredMentionUsers.value]);

const getActiveTextRef = () => mentionActiveInput.value === "thread" ? threadTextareaRef : textareaRef;
const getActiveText = () => mentionActiveInput.value === "thread" ? threadMessageText : messageText;

const checkForMention = (source = "main") => {
  mentionActiveInput.value = source;
  const text = getActiveText().value || "";
  const tAreaRef = getActiveTextRef();
  const tArea = tAreaRef.value?.$el?.querySelector("textarea") || tAreaRef.value;
  const cursorPos = tArea?.selectionStart ?? text.length;
  const textBeforeCursor = text.substring(0, cursorPos);
  const lastAt = textBeforeCursor.lastIndexOf("@");

  if (lastAt === -1) { closeMentionDropdown(); return; }

  const textAfterAt = textBeforeCursor.substring(lastAt + 1);
  if (!textAfterAt.includes(" ")) {
    mentionStartPos.value = lastAt;
    mentionSearch.value = textAfterAt;
    showMentionDropdown.value = true;
    selectedMentionIndex.value = 0;
    nextTick(() => {
      if (tArea) {
        const rect = tArea.getBoundingClientRect();
        mentionDropdownDirection.value = rect.top < 360 ? "down" : "up";
      }
      if (mentionSearchRef.value) {
        mentionSearchRef.value.focus();
      }
    });
  } else {
    closeMentionDropdown();
  }
};

const closeMentionDropdown = () => {
  showMentionDropdown.value = false;
  mentionSearch.value = "";
  selectedMentionIndex.value = 0;
  nextTick(() => {
    const tAreaRef = getActiveTextRef();
    const tArea = tAreaRef.value?.$el?.querySelector("textarea") || tAreaRef.value;
    if (tArea?.focus) tArea.focus();
  });
};

const handleClickOutsideMention = (event) => {
  if (!showMentionDropdown.value) return;
  if (mentionDropdownRef.value?.contains(event.target)) return;
  const tAreaRef = getActiveTextRef();
  const tArea = tAreaRef.value?.$el?.querySelector("textarea") || tAreaRef.value;
  if (tArea?.contains(event.target)) return;
  closeMentionDropdown();
};

const navigateMentions = (direction) => {
  const maxIndex = mentionItems.value.length - 1;
  let newIndex = selectedMentionIndex.value + direction;
  if (newIndex < 0) newIndex = maxIndex;
  if (newIndex > maxIndex) newIndex = 0;
  selectedMentionIndex.value = newIndex;
};

const selectMention = () => {
  if (mentionItems.value.length === 0) return;
  const item = mentionItems.value[selectedMentionIndex.value];
  if (!item) return;

  const activeTextRef = getActiveTextRef();
  const activeText = getActiveText();
  const tArea = activeTextRef.value?.$el?.querySelector("textarea") || activeTextRef.value;
  const cursorPos = tArea?.selectionStart || activeText.value.length;
  const beforeMention = activeText.value.substring(0, mentionStartPos.value);
  const afterCursor = activeText.value.substring(cursorPos);

  const isThread = mentionActiveInput.value === "thread";
  const activeMentionIds = isThread ? threadMentionedUserIds : mentionedUserIds;
  const activeGroupMention = isThread ? threadHasGroupMention : hasGroupMention;

  if (item.isGroupMention) {
    activeText.value = beforeMention + "@" + item.name + " " + afterCursor;
    activeGroupMention.value = true;
  } else {
    const userName = renderEntity(item);
    activeText.value = beforeMention + "@" + userName + " " + afterCursor;
    if (!activeMentionIds.value.includes(item.id)) {
      activeMentionIds.value.push(item.id);
    }
  }

  closeMentionDropdown();
  nextTick(() => {
    if (tArea?.focus) tArea.focus();
  });
};
const editingThreadMessageId = ref(null);
const editingThreadOriginalText = ref("");
const hoveredMessageId = ref(null);
const isOnActions = ref(false);
const isDropdownOpen = ref(false);
const dropdownMessageId = ref(null);

const activeMessageId = computed(() => {
  if (isDropdownOpen.value && dropdownMessageId.value) {
    return dropdownMessageId.value;
  }
  return hoveredMessageId.value;
});

const handleMessageLeave = () => {
  if (!isOnActions.value && !isDropdownOpen.value) {
    hoveredMessageId.value = null;
  }
};

const handleActionsLeave = () => {
  isOnActions.value = false;
  if (!isDropdownOpen.value) {
    hoveredMessageId.value = null;
  }
};

const handleDropdownOpen = (messageId) => {
  isDropdownOpen.value = true;
  dropdownMessageId.value = messageId;
};

const handleDropdownClose = () => {
  isDropdownOpen.value = false;
  dropdownMessageId.value = null;
  hoveredMessageId.value = null;
  isOnActions.value = false;
};

const {
  attachments,
  uploadFiles,
  removeAttachment,
  resetAttachments,
} = useFileUpload({
  api: uploadDownloadAPITypes.MARKETING_UPLOADS,
});

const {
  attachments: threadAttachments,
  uploadFiles: uploadThreadFiles,
  removeAttachment: removeThreadAttachment,
  resetAttachments: resetThreadAttachments,
} = useFileUpload({
  api: uploadDownloadAPITypes.MARKETING_UPLOADS,
});

// Drag-and-drop
const isDragOver = ref(false);
let dragCounter = 0;

const onDragEnter = (e) => {
  if (!e.dataTransfer?.types?.includes("Files")) return;
  dragCounter++;
  isDragOver.value = true;
};

const onDragLeave = () => {
  dragCounter--;
  if (dragCounter <= 0) {
    dragCounter = 0;
    isDragOver.value = false;
  }
};

const onDrop = (e) => {
  dragCounter = 0;
  isDragOver.value = false;
  const files = e.dataTransfer?.files;
  if (files?.length > 0) {
    if (props.chatEntry.threadOpen) {
      uploadThreadFiles(files);
    } else {
      uploadFiles(files);
    }
  }
};

const disableSendButton = computed(() => {
  const hasText = messageText.value?.trim()?.length > 0;
  const hasAttachments = attachments.value && attachments.value.length > 0;
  return !hasText && !hasAttachments;
});


const filteredMessages = computed(() => filterAndSortMessages(props.chatEntry.messages));

const threadMessages = computed(() => {
  return [...(props.chatEntry.threadMessages || [])]
    .sort((a, b) => new Date(a.sent_at) - new Date(b.sent_at));
});

const isGroupedMessage = (index, isThread = false) => {
  const msgs = isThread ? threadMessages.value : filteredMessages.value;
  return isGroupedMsg(msgs, index);
};

const shouldShowDateDivider = (index) => showDateDivider(filteredMessages.value, index);

const handleMessageContentClick = (event, message) => {
  try {
    const target = event.target;

    if (target.tagName === "A" && target.href) {
      event.preventDefault();
      window.open(target.href, "_blank", "noopener,noreferrer");
      return;
    }

    if (target.classList.contains("mention-link")) {
      const userId = target.getAttribute("data-user-id");
      if (userId) {
        const mentionedUsers = message.mentioned_users || [];
        const user = mentionedUsers.find((u) => Number(u.id) === Number(userId));
        if (user) {
          handleMentionClick(user);
        }
      }
    }
  } catch (error) {
    console.error("Error handling message content click:", error);
  }
};

const handleMentionClick = (user) => {
  try {
    if (!user || !user.id) return;

    const allGroups = store.getters["chats/groups"] || [];
    const userName = renderEntity(user).toLowerCase().trim();
    const userId = Number(user.id);

    const existingGroup = allGroups.find((group) => {
      if (!group.is_dm || group.recipient_type !== 1) return false;
      if (String(group.id) === `user_${userId}`) return true;
      if ((group.type === "dm" || group.type === "self_dm") && group.name && group.name.toLowerCase().trim() === userName) return true;
      return false;
    });

    if (existingGroup) {
      store.dispatch("embeddedChats/openChat", existingGroup);
    } else {
      store.dispatch("embeddedChats/openChat", createPlaceholderDM(user, renderEntity));
    }
  } catch (error) {
    console.error("Error handling mention click:", error);
  }
};

const handleDownloadAttachment = (attachment) => {
  downloadAttachment(attachment, (errorMsg) => {
    message.error(errorMsg);
  });
};


// Draft management
const saveDraft = (chatId) => {
  if (!chatId) return;
  const text = messageText.value?.trim();
  if (!text) {
    localStorage.removeItem(`chat_draft_${chatId}`);
    eventBus.emit("draft-updated");
    return;
  }
  const draft = {
    text: messageText.value,
    mentionedUserIds: mentionedUserIds.value,
    hasGroupMention: hasGroupMention.value,
    timestamp: Date.now(),
  };
  localStorage.setItem(`chat_draft_${chatId}`, JSON.stringify(draft));
  eventBus.emit("draft-updated");
};

const loadDraft = (chatId) => {
  if (!chatId) return;
  try {
    const draftJson = localStorage.getItem(`chat_draft_${chatId}`);
    if (draftJson) {
      const draft = JSON.parse(draftJson);
      messageText.value = draft.text || "";
      mentionedUserIds.value = draft.mentionedUserIds || [];
      hasGroupMention.value = draft.hasGroupMention || false;
    } else {
      messageText.value = "";
      mentionedUserIds.value = [];
      hasGroupMention.value = false;
    }
  } catch {
    messageText.value = "";
  }
};

const clearDraft = (chatId) => {
  if (!chatId) return;
  localStorage.removeItem(`chat_draft_${chatId}`);
  eventBus.emit("draft-updated");
};

const saveDraftDebounced = debounce(() => {
  if (chat.value?.id && !editingMessageId.value) {
    saveDraft(chat.value.id);
  }
}, 1000);

const reactionUsersCache = ref({});

const getReactionCacheKey = (messageId, emoji) => `${messageId}:${emoji}`;

const getReactionTooltip = (messageId, emoji) => {
  const key = getReactionCacheKey(messageId, emoji);
  const cached = reactionUsersCache.value[key];
  if (!cached) return "Loading...";
  if (cached.length === 0) return "";
  return cached.join(", ");
};

const fetchReactionUsers = async (messageId, emoji) => {
  if (!chat.value?.id) return;
  const key = getReactionCacheKey(messageId, emoji);
  if (reactionUsersCache.value[key]) return;
  try {
    const response = await ChatService.fetchReactionUsers(messageId, chat.value.id, emoji);
    if (response?.success && response?.users) {
      reactionUsersCache.value = { ...reactionUsersCache.value, [key]: response.users };
    }
  } catch (error) {
    console.error("Failed to fetch reaction users:", error);
  }
};

const handleReactionClick = async (messageId, emoji) => {
  // Clear tooltip cache for this reaction
  const cacheKey = getReactionCacheKey(messageId, emoji);
  delete reactionUsersCache.value[cacheKey];
  if (!chat.value?.id || !isCurrentUserMember.value) return;
  try {
    const response = await ChatService.addReaction(messageId, chat.value.id, emoji);
    if (response?.success) {
      const delta = response.status === 201 ? 1 : -1;
      store.commit("embeddedChats/UPDATE_REACTION", { messageId, emoji, delta });
    }
  } catch (error) {
    console.error("Error toggling reaction:", error);
  }
};

const scrollToBottom = () => {
  nextTick(() => {
    if (messagesContainer.value) {
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
    }
  });
};

const scrollThreadToBottom = () => {
  nextTick(() => {
    if (threadMessagesContainer.value) {
      threadMessagesContainer.value.scrollTop = threadMessagesContainer.value.scrollHeight;
    }
  });
};

const threadRootCache = ref(new Map());
const pendingThreadFetches = new Set();

const getThreadRootId = (message) => {
  if (message?._resolvedThreadRoot) return message._resolvedThreadRoot;
  const broadcastId = message?.broadcast_from_thread_id;
  if (!broadcastId) return message?.thread_id || message?.id;

  // Check cache
  if (threadRootCache.value.has(broadcastId)) {
    const cached = threadRootCache.value.get(broadcastId);
    return cached.thread_id || broadcastId;
  }

  const allMsgs = props.chatEntry.messages || [];
  const replyMsg = allMsgs.find((m) => String(m.id) === String(broadcastId));
  if (replyMsg?.thread_id) return replyMsg.thread_id;

  // Fetch if not found
  fetchThreadRootMessage(broadcastId);
  return broadcastId;
};

const getThreadMessagePreview = (message) => {
  const rootId = getThreadRootId(message);
  if (!rootId) return "Original message";

  // Check cache for root message
  if (threadRootCache.value.has(rootId)) {
    return threadRootCache.value.get(rootId)?.text || "Original message";
  }

  // Search in loaded messages — only match root (where id === thread_id or id === rootId)
  const allMsgs = props.chatEntry.messages || [];
  const rootMsg = allMsgs.find((m) => String(m.id) === String(rootId));
  if (rootMsg?.text) {
    threadRootCache.value.set(rootId, rootMsg);
    return rootMsg.text;
  }

  // Fetch root message
  fetchThreadRootMessage(rootId);
  return "Loading...";
};

const fetchThreadRootMessage = async (messageId) => {
  if (!messageId || !chat.value?.id) return;
  if (pendingThreadFetches.has(messageId)) return;
  pendingThreadFetches.add(messageId);

  try {
    const response = await ChatService.fetchMessageById(messageId, chat.value.id);
    if (response?.success && response?.message) {
      threadRootCache.value.set(messageId, response.message);
      // If this was a reply, also cache the mapping and fetch the root
      if (response.message.thread_id && String(response.message.thread_id) !== String(messageId)) {
        threadRootCache.value.set(response.message.broadcast_from_thread_id || messageId, response.message);
        if (!threadRootCache.value.has(response.message.thread_id)) {
          pendingThreadFetches.delete(messageId);
          fetchThreadRootMessage(response.message.thread_id);
          return;
        }
      }
    }
  } catch (error) {
    console.error("Error fetching thread root message:", error);
  } finally {
    pendingThreadFetches.delete(messageId);
  }
};

const openThread = (message) => {
  cancelThreadEdit();
  alsoSendToChannel.value = false;
  if (messagesContainer.value) {
    savedScrollPosition.value = messagesContainer.value.scrollTop;
  }
  const threadId = message.thread_id || message.id;
  store.dispatch("embeddedChats/openThread", { chatId: chat.value.id, threadId });
  nextTick(() => scrollThreadToBottom());
};

const openThreadFromBroadcast = async (message) => {
  cancelThreadEdit();
  alsoSendToChannel.value = false;
  if (messagesContainer.value) {
    savedScrollPosition.value = messagesContainer.value.scrollTop;
  }
  let threadId = getThreadRootId(message);

  // If getThreadRootId returned the broadcastId fallback, resolve the real root
  if (threadId && message?.broadcast_from_thread_id && threadId === message.broadcast_from_thread_id) {
    try {
      const response = await ChatService.fetchMessageById(message.broadcast_from_thread_id, chat.value.id);
      if (response?.success && response?.message?.thread_id) {
        threadId = response.message.thread_id;
        threadRootCache.value.set(message.broadcast_from_thread_id, response.message);
      }
    } catch (error) {
      console.error("Error resolving thread root:", error);
    }
  }

  if (threadId) {
    store.dispatch("embeddedChats/openThread", { chatId: chat.value.id, threadId });
    nextTick(() => scrollThreadToBottom());
  }
};

const closeThread = () => {
  cancelThreadEdit();
  alsoSendToChannel.value = false;
  resetThreadAttachments();
  threadMentionedUserIds.value = [];
  threadHasGroupMention.value = false;
  closeMentionDropdown();
  store.dispatch("embeddedChats/closeThread", { chatId: chat.value.id });
  nextTick(() => {
    if (messagesContainer.value && savedScrollPosition.value !== null) {
      messagesContainer.value.scrollTop = savedScrollPosition.value;
      savedScrollPosition.value = null;
    }
  });
};

const startThreadEdit = (messageId, text) => {
  editingThreadMessageId.value = messageId;
  editingThreadOriginalText.value = text;
  threadMessageText.value = text;
  nextTick(() => {
    threadTextareaRef.value?.focus?.();
  });
};

const cancelThreadEdit = () => {
  editingThreadMessageId.value = null;
  editingThreadOriginalText.value = "";
  threadMessageText.value = "";
};

const handleThreadKeyDown = (e) => {
  if (showMentionDropdown.value) {
    if (e.key === "ArrowDown") { e.preventDefault(); navigateMentions(1); }
    else if (e.key === "ArrowUp") { e.preventDefault(); navigateMentions(-1); }
    else if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); selectMention(); }
    else if (e.key === "Escape") { closeMentionDropdown(); }
  } else if (e.key === "Enter" && !e.shiftKey) {
    e.preventDefault();
    sendThreadReply();
  } else if (e.key === "Escape" && editingThreadMessageId.value) {
    cancelThreadEdit();
  }
};

const handleThreadFileSelect = async (files) => {
  try {
    await uploadThreadFiles(files);
  } catch (error) {
    console.error("Error uploading thread files:", error);
  }
};

const handleThreadPaste = async (e) => {
  const files = e.clipboardData?.files;
  if (files?.length > 0) {
    e.preventDefault();
    try {
      await uploadThreadFiles(files);
    } catch (error) {
      console.error("Error uploading pasted thread files:", error);
    }
  }
};

const insertThreadEmoji = (emoji) => {
  const emojiChar = emoji.i || emoji.native || emoji;
  if (!emojiChar) return;
  threadMessageText.value = (threadMessageText.value || "") + emojiChar;
  showThreadEmojiPicker.value = false;
};

const sendThreadReply = async () => {
  const text = threadMessageText.value?.trim();

  // Handle edit mode before empty-text guard
  if (editingThreadMessageId.value) {
    if (!text) {
      cancelThreadEdit();
      return;
    }
    if (text === editingThreadOriginalText.value) {
      cancelThreadEdit();
      return;
    }
    try {
      const response = await ChatService.editMessage(editingThreadMessageId.value, chat.value.id, text);
      if (response?.success && response?.message) {
        store.commit("embeddedChats/UPDATE_MESSAGE", {
          chatId: chat.value.id,
          message: response.message,
        });
      }
    } catch (error) {
      console.error("Error editing thread message:", error);
    }
    cancelThreadEdit();
    return;
  }

  const formattedThreadAttachments = formatAttachmentsForAPI(threadAttachments.value);
  if (!text && formattedThreadAttachments.length === 0) return;
  if (!chat.value?.id || !props.chatEntry.threadId) return;

  const recipientType = getRecipientType(chat.value.type);
  const payload = {
    recipient_type: recipientType,
    recipient_id: parseInt(String(chat.value.id).replace(/^group_/, ''), 10),
    thread_id: props.chatEntry.threadId,
    text: text || "",
    attachments: formattedThreadAttachments,
    mentions: threadHasGroupMention.value ? ["here"] : [...threadMentionedUserIds.value],
    mention_groups: [],
  };

  if (alsoSendToChannel.value) {
    payload.also_send_to_channel = true;
  }

  threadMessageText.value = "";
  resetThreadAttachments();
  threadMentionedUserIds.value = [];
  threadHasGroupMention.value = false;

  try {
    const response = await ChatService.createMessage(payload);
    if (response?.success && response?.message) {
      store.commit("embeddedChats/ADD_THREAD_MESSAGE", {
        chatId: chat.value.id,
        message: response.message,
      });
      store.commit("embeddedChats/UPDATE_REPLY_COUNT", {
        chatId: chat.value.id,
        threadId: props.chatEntry.threadId,
        delta: 1,
      });
      // If broadcast to channel, re-fetch messages to pick up the broadcast copy
      if (payload.also_send_to_channel) {
        store.dispatch("embeddedChats/fetchMessages", chat.value.id);
      }
      alsoSendToChannel.value = false;
      scrollThreadToBottom();
    }
  } catch (error) {
    console.error("Error sending thread reply:", error);
  }
};

const handleScroll = async () => {
  if (!messagesContainer.value || props.chatEntry.loadingMore || !props.chatEntry.hasMore) return;
  if (messagesContainer.value.scrollTop <= 100) {
    isLoadingOlder.value = true;
    const previousScrollHeight = messagesContainer.value.scrollHeight;
    await store.dispatch("embeddedChats/fetchOlderMessages", chat.value.id);
    nextTick(() => {
      if (messagesContainer.value) {
        const newScrollHeight = messagesContainer.value.scrollHeight;
        messagesContainer.value.scrollTop = newScrollHeight - previousScrollHeight;
      }
      isLoadingOlder.value = false;
    });
  }
};

const toggleChat = () => {
  saveDraftDebounced.flush();
  saveDraft(chat.value?.id);
  store.dispatch("embeddedChats/toggleChat", chat.value.id);
};

const updateSidebarLastMessage = () => {
  if (!chat.value?.id) return;
  const msgs = filteredMessages.value;
  if (msgs.length === 0) {
    store.dispatch("chats/updateSidebarGroup", {
      groupId: chat.value.id,
      lastMessage: "",
      senderId: null,
    });
    return;
  }
  const lastMsg = msgs[msgs.length - 1];
  let lastText = lastMsg.text || "";
  if (!lastText && lastMsg.attachments?.length > 0) {
    const fileName = lastMsg.attachments[0]?.file_name || lastMsg.attachments[0]?.name;
    lastText = fileName ? `📎 ${fileName}` : "📎 Attachment";
  }
  store.dispatch("chats/updateSidebarGroup", {
    groupId: chat.value.id,
    lastMessage: lastText,
    lastMessageAttachments: lastMsg.attachments,
    senderId: lastMsg.user_id || lastMsg.sender?.id,
    lastMessageTime: lastMsg.sent_at,
  });
};

const closeChat = () => {
  saveDraftDebounced.cancel();
  saveDraft(chat.value?.id);
  store.dispatch("embeddedChats/closeChat", chat.value.id);
};

const handleKeyDown = (e) => {
  if (showMentionDropdown.value) {
    if (e.key === "ArrowDown") { e.preventDefault(); navigateMentions(1); }
    else if (e.key === "ArrowUp") { e.preventDefault(); navigateMentions(-1); }
    else if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); selectMention(); }
    else if (e.key === "Escape") { closeMentionDropdown(); }
  } else if (e.key === "Enter" && !e.shiftKey) {
    e.preventDefault();
    sendMessage();
  } else if (e.key === "Escape" && editingMessageId.value) {
    cancelEdit();
  }
};

const formatAttachmentsForAPI = (atts) => {
  if (!atts || atts.length === 0) return [];
  return atts.map((attachment) => {
    const payload = {
      url: attachment.url,
      media_type: attachment.content_type || "application/octet-stream",
    };
    if (attachment.file_name) {
      payload.file_name = attachment.file_name;
    }
    return payload;
  }).filter((a) => a.url);
};

const handleFileSelect = async (files) => {
  try {
    await uploadFiles(files);
  } catch (error) {
    console.error("Error uploading files:", error);
  }
};

const handlePaste = async (e) => {
  const files = e.clipboardData?.files;
  if (files?.length > 0) {
    e.preventDefault();
    try {
      await uploadFiles(files);
    } catch (error) {
      console.error("Error uploading pasted files:", error);
    }
  }
};

const insertEmoji = (emoji) => {
  const emojiChar = emoji.i || emoji.native || emoji;
  if (!emojiChar) return;
  messageText.value = (messageText.value || "") + emojiChar;
  showEmojiPicker.value = false;
};

const sendMessage = async () => {
  const text = messageText.value?.trim();
  const formattedAttachments = formatAttachmentsForAPI(attachments.value);

  // Handle edit mode before empty-text guard
  if (editingMessageId.value) {
    if (!text) {
      cancelEdit();
      return;
    }
    if (text === editingOriginalText.value) {
      cancelEdit();
      return;
    }
    try {
      const response = await ChatService.editMessage(editingMessageId.value, chat.value.id, text);
      if (response?.success && response?.message) {
        store.commit("embeddedChats/UPDATE_MESSAGE", {
          chatId: chat.value.id,
          message: response.message,
        });
        nextTick(() => updateSidebarLastMessage());
      }
    } catch (error) {
      console.error("Error editing embedded chat message:", error);
    }
    cancelEdit();
    return;
  }

  if (!text && formattedAttachments.length === 0) return;
  if (!chat.value?.id) return;

  if (chat.value.is_placeholder) {
    await sendPlaceholderMessage(text, formattedAttachments);
    return;
  }

  const recipientType = getRecipientType(chat.value.type);
  const mentionsPayload = hasGroupMention.value ? ["here"] : [...mentionedUserIds.value];
  const payload = {
    recipient_type: recipientType,
    recipient_id: parseInt(String(chat.value.id).replace(/^group_/, ''), 10),
    thread_id: null,
    text: text || "",
    attachments: formattedAttachments,
    mentions: mentionsPayload,
    mention_groups: [],
  };

  messageText.value = "";
  resetAttachments();
  mentionedUserIds.value = [];
  hasGroupMention.value = false;

  try {
    const response = await ChatService.createMessage(payload);
    if (response?.success && response?.message) {
      store.commit("embeddedChats/ADD_MESSAGE", {
        chatId: chat.value.id,
        message: response.message,
      });
      scrollToBottom();
      clearDraft(chat.value.id);
      nextTick(() => updateSidebarLastMessage());

      // Notify pending invitees of external groups (best-effort)
      if (chat.value?.type === chatGroupVisibilityOptions.EXTERNAL) {
        DashboardService.notifyExternalGroupPendingInvitees(
          chat.value.id,
          text || "(attachment)",
        ).catch(() => {});
      }
    }
  } catch (error) {
    console.error("Error sending embedded chat message:", error);
  }
};

const sendPlaceholderMessage = async (text, formattedAttachments) => {
  const oldChatId = chat.value.id;
  const placeholderUsers = chat.value.placeholder_users;
  const isGroupDM = placeholderUsers && placeholderUsers.length >= 2;

  let recipientType;
  let userIds;
  let groupName;

  if (isGroupDM) {
    recipientType = 2;
    userIds = placeholderUsers.map((u) => u.id);
    groupName = placeholderUsers.map((u) => renderEntity(u)).join(", ");
  } else {
    recipientType = 1;
    const otherUser = chat.value.other_user || chat.value.user_data;
    const placeholderUserId = otherUser?.id
      || (String(oldChatId).startsWith("user_") ? Number(String(oldChatId).replace("user_", "")) : null);
    userIds = [placeholderUserId];
    groupName = otherUser ? renderEntity(otherUser) : chat.value.name || null;
  }

  messageText.value = "";
  resetAttachments();

  try {
    const payload = {
      recipient_type: recipientType,
      recipient_id: null,
      thread_id: null,
      text: text || "",
      attachments: formattedAttachments,
      mentions: [],
      mention_groups: [],
      user_ids: userIds,
      group_name: groupName,
    };

    const response = await ChatService.createMessage(payload);

    if (response?.success) {
      let newGroup = response?.group || response?.conversation;
      const newMessage = response?.message;

      if (!newGroup && newMessage?.recipient_id) {
        const fetchResult = await store.dispatch("chats/fetchGroups");
        if (fetchResult?.success && fetchResult?.groups) {
          newGroup = fetchResult.groups.find(
            (g) => String(g.id) === String(newMessage.recipient_id),
          );
        }
      }

      if (newGroup) {
        // Replace the placeholder with the real chat in embedded store
        store.commit("embeddedChats/REPLACE_CHAT", { oldChatId, newChat: newGroup });

        // Add the real group to the main sidebar list
        await store.dispatch("chats/addGroupToList", newGroup);

        // Fetch messages and members for the new real chat
        store.dispatch("embeddedChats/fetchMessages", newGroup.id);
        store.dispatch("embeddedChats/fetchGroupMembers", newGroup.id);
      } else if (newMessage) {
        store.commit("embeddedChats/ADD_MESSAGE", {
          chatId: oldChatId,
          message: newMessage,
        });
      }

      scrollToBottom();
    }
  } catch (error) {
    console.error("Error sending placeholder message:", error);
  }
};

const handleMessageDeleted = (messageId) => {
  if (!messageId || !chat.value?.id) return;
  const entry = props.chatEntry;
  const chatId = chat.value.id;

  // Check if deleted message is a thread reply and decrement parent's reply count
  const threadMsg = entry.threadMessages.find((m) => String(m.id) === String(messageId));
  if (threadMsg && threadMsg.thread_id && String(threadMsg.thread_id) !== String(threadMsg.id)) {
    store.commit("embeddedChats/UPDATE_REPLY_COUNT", {
      chatId,
      threadId: threadMsg.thread_id,
      delta: -1,
    });
  }

  const hasMessage = entry.messages.some((m) => String(m.id) === String(messageId))
    || threadMsg;
  if (hasMessage) {
    // Reset hover state if the deleted message was hovered
    if (String(hoveredMessageId.value) === String(messageId)) {
      hoveredMessageId.value = null;
      isOnActions.value = false;
      isDropdownOpen.value = false;
      dropdownMessageId.value = null;
    }
    store.commit("embeddedChats/DELETE_MESSAGE", { chatId, messageId });
    nextTick(() => updateSidebarLastMessage());
  }
};

const startEditMessage = (messageId, text) => {
  editingMessageId.value = messageId;
  editingOriginalText.value = text;
  messageText.value = text;
  nextTick(() => {
    textareaRef.value?.focus?.();
  });
};

const cancelEdit = () => {
  editingMessageId.value = null;
  editingOriginalText.value = "";
  messageText.value = "";
};

const handleMembersUpdated = () => {
  if (chat.value?.id && !chat.value?.is_placeholder) {
    store.dispatch("embeddedChats/fetchGroupMembers", chat.value.id);
  }
};

const handleGroupDeleted = () => {
  if (chat.value?.id) {
    store.dispatch("embeddedChats/closeChat", chat.value.id);
  }
};

const handleCreateTask = (data) => {
  // Only handle events from embedded chat sources
  const source = typeof data === "object" ? data.source : null;
  if (source !== "embedded_chat" && source !== "embedded_thread") return;

  const msgId = typeof data === "object" ? data.messageId : data;

  // Only handle if the message belongs to this specific embedded chat
  const allMessages = [...(props.chatEntry.messages || []), ...(props.chatEntry.threadMessages || [])];
  const msgBelongsToThisChat = allMessages.some(m => String(m.id) === String(msgId));
  if (!msgBelongsToThisChat) return;

  taskMessageId.value = msgId;

  if (typeof data === "object") {
    const messageText = data.messageContent || "";

    taskInitialData.value = {
    };

    const msg = allMessages.find(m => String(m.id) === String(msgId));
    if (msg) {
      taskMessageContext.value = { text: msg.text || "", sender: msg.sender || null, sentAt: msg.sent_at || null };
    } else {
      taskMessageContext.value = { text: messageText, sender: null, sentAt: null };
    }
  } else {
    taskInitialData.value = null;
    taskMessageContext.value = null;
  }

  showTaskModal.value = true;
};

watch(showTaskModal, (newVal, oldVal) => {
  if (oldVal === true && newVal === false && taskMessageId.value) {
    eventBus.emit("task-modal-closed", taskMessageId.value);
    taskMessageId.value = null;
    taskInitialData.value = null;
    taskMessageContext.value = null;
  }
});

onMounted(() => {
  scrollToBottom();
  eventBus.on("message-deleted", handleMessageDeleted);
  eventBus.on("group-members-updated", handleMembersUpdated);
  eventBus.on("group-deleted", handleGroupDeleted);
  eventBus.on("create-task", handleCreateTask);
  document.addEventListener("click", handleClickOutsideMention);
});

onBeforeUnmount(() => {
  saveDraftDebounced.flush();
  saveDraft(chat.value?.id);
  eventBus.off("message-deleted", handleMessageDeleted);
  eventBus.off("group-members-updated", handleMembersUpdated);
  eventBus.off("group-deleted", handleGroupDeleted);
  eventBus.off("create-task", handleCreateTask);
  document.removeEventListener("click", handleClickOutsideMention);
});

watch(
  () => props.chatEntry.messages,
  () => {
    if (!isLoadingOlder.value) {
      nextTick(() => scrollToBottom());
    }
  },
);

watch(
  () => props.chatEntry.expanded,
  (expanded) => {
    if (expanded) {
      nextTick(() => scrollToBottom());
    }
  },
);

watch(
  () => props.chatEntry.threadMessages,
  () => {
    nextTick(() => scrollThreadToBottom());
  },
);

watch(messageText, (newValue) => {
  setTimeout(() => checkForMention(), 10);

  if (hasGroupMention.value) {
    const text = (newValue || "").toLowerCase();
    if (!/(^|[\s])@(here|everyone)\b/.test(text)) {
      hasGroupMention.value = false;
    }
  }

  saveDraftDebounced();
}, { flush: 'post' });

watch(threadMessageText, (newValue) => {
  setTimeout(() => checkForMention("thread"), 10);

  if (threadHasGroupMention.value) {
    const text = (newValue || "").toLowerCase();
    if (!/(^|[\s])@(here|everyone)\b/.test(text)) {
      threadHasGroupMention.value = false;
    }
  }
}, { flush: 'post' });

// Load draft when chat is first opened or expanded
watch(
  () => chat.value?.id,
  (newId) => {
    if (newId && !chat.value?.is_placeholder) {
      loadDraft(newId);
    }
  },
  { immediate: true },
);
</script>

<style scoped>
.embedded_chat_window {
  box-shadow: var(--dropdown-boxshadow);
  border: 1px solid var(--white-opacity-8);
}

.embedded_chat_body {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: height 0.3s ease, opacity 0.25s ease;
  background-color: var(--background-color);
  border: 1px solid var(--white-opacity-8);
  border-top: none;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}

.embedded_chat_body--open {
  opacity: 1;
}

.embedded_chat_body--closed {
  height: 0 !important;
  opacity: 0;
  border: none;
  box-shadow: none;
}
</style>
